import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Load from '../../components/Load';
import LeagueMembersList from '../../components/LeagueMembersList';
import { PageBreadcrumb } from '../../components/navigation';
import { useProfile } from '../../context/ProfileContext';
import { useDebounce } from '../../hooks/useDebounce';
import {
  organizadoresPorLiga,
  organizadoresPorTorneio,
  type Organizador,
} from '../../services/organizadorService';
import { buscarSeguidoresDaLiga, buscarJogadoresDaLiga } from '../../services/ligaService';
import { buscarTorneiosPorLiga } from '../../services/torneioService';
import { buscarInscricoesPorLigaAgrupadas } from '../../services/inscricaoTorneioService';
import {
  buscarAlunosDaLiga,
  marcarAluno,
  desmarcarAluno,
  marcarAlunosEmMassa,
  desmarcarAlunosEmMassa,
} from '../../services/alunoLigaService';
import {
  organizadoresToMembers,
  atletasToMembers,
  mergeMembersForAllTab,
  enrichMembersWithTorneios,
  type LeagueMember,
  type TorneioInscrito,
} from './utils/leagueMembersUtils';

type TabId = 'todos' | 'colaboradores' | 'seguidores' | 'jogadores';

const TABS: { id: TabId; label: string }[] = [
  { id: 'todos', label: 'Todos' },
  { id: 'colaboradores', label: 'Colaboradores' },
  { id: 'seguidores', label: 'Seguidores' },
  { id: 'jogadores', label: 'Jogadores' },
];

const EMPTY_MESSAGES: Record<TabId, string> = {
  todos: 'Nenhum membro encontrado.',
  colaboradores: 'Nenhum colaborador encontrado.',
  seguidores: 'Nenhum seguidor encontrado.',
  jogadores: 'Nenhum jogador encontrado.',
};

const EMPTY_TORNEIOS_MAP = new Map<string, TorneioInscrito[]>();

const LeagueMembersPage: React.FC = () => {
  const { currentProfile, isLoading: profileLoading } = useProfile();
  const navigate = useNavigate();
  const ligaId = currentProfile?.tipo === 'LIGA' ? currentProfile.referenciaId : null;

  const [activeTab, setActiveTab] = useState<TabId>('todos');
  const [nameFilter, setNameFilter] = useState('');
  const debouncedFilter = useDebounce(nameFilter, 300);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [colaboradoresRaw, setColaboradoresRaw] = useState<Organizador[]>([]);
  const [seguidoresRaw, setSeguidoresRaw] = useState<Awaited<ReturnType<typeof buscarSeguidoresDaLiga>>>([]);
  const [jogadoresRaw, setJogadoresRaw] = useState<Awaited<ReturnType<typeof buscarJogadoresDaLiga>>>([]);
  const [torneiosPorAtleta, setTorneiosPorAtleta] = useState(EMPTY_TORNEIOS_MAP);
  const [alunosIds, setAlunosIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [savingAluno, setSavingAluno] = useState<string | null>(null);

  useEffect(() => {
    if (!ligaId || !currentProfile || currentProfile.tipo !== 'LIGA') return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const [orgsLiga, seguidores, jogadores] = await Promise.all([
          organizadoresPorLiga(ligaId),
          buscarSeguidoresDaLiga(ligaId),
          buscarJogadoresDaLiga(ligaId),
        ]);

        if (cancelled) return;

        let allOrgs = [...orgsLiga];
        try {
          const torneios = await buscarTorneiosPorLiga(ligaId);
          for (const t of torneios) {
            const orgsTorneio = await organizadoresPorTorneio(t.id);
            for (const org of orgsTorneio) {
              const exists = allOrgs.some(
                (o) => o.usuario?.id === org.usuario?.id
              );
              if (!exists) allOrgs.push(org);
            }
          }
        } catch {
          // Se falhar ao buscar torneios/organizadores por torneio, usa só organizadores da liga
        }

        if (cancelled) return;

        setColaboradoresRaw(allOrgs);
        setSeguidoresRaw(seguidores);
        setJogadoresRaw(jogadores);

        try {
          const alunosRaw = await buscarAlunosDaLiga(ligaId);
          if (!cancelled) setAlunosIds(new Set(alunosRaw));
        } catch { /* degradação graciosa */ }

        try {
          const inscricoesAgrupadas = await buscarInscricoesPorLigaAgrupadas(ligaId);
          const map = new Map<string, TorneioInscrito[]>();
          for (const item of inscricoesAgrupadas) {
            map.set(
              item.atletaId,
              item.torneios.map(t => ({ id: t.torneioId, nome: t.torneioNome }))
            );
          }
          if (!cancelled) setTorneiosPorAtleta(map);
        } catch {
          // Se falhar, membros são exibidos sem torneios — degradação graciosa
        }
      } catch (e: any) {
        if (!cancelled) {
          const msg =
            e?.graphQLErrors?.[0]?.message ||
            e?.message ||
            'Erro ao carregar membros. Tente novamente.';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ligaId, currentProfile]);

  const colaboradores = useMemo(
    () => organizadoresToMembers(colaboradoresRaw),
    [colaboradoresRaw]
  );
  const seguidores = useMemo(
    () => atletasToMembers(seguidoresRaw, 'seguidor'),
    [seguidoresRaw]
  );
  const jogadores = useMemo(
    () => enrichMembersWithTorneios(
      atletasToMembers(jogadoresRaw, 'jogador'),
      torneiosPorAtleta
    ),
    [jogadoresRaw, torneiosPorAtleta]
  );
  const todos = useMemo(
    () => mergeMembersForAllTab(colaboradores, seguidores, jogadores),
    [colaboradores, seguidores, jogadores]
  );

  const membersByTab: Record<TabId, LeagueMember[]> = useMemo(
    () => ({
      todos,
      colaboradores,
      seguidores,
      jogadores,
    }),
    [todos, colaboradores, seguidores, jogadores]
  );

  const currentMembers = membersByTab[activeTab];

  const filteredMembers = useMemo(() => {
    if (!debouncedFilter.trim()) return currentMembers;
    const lower = debouncedFilter.toLowerCase();
    return currentMembers.filter(
      (m) =>
        m.nome.toLowerCase().includes(lower) ||
        (m.username && m.username.toLowerCase().includes(lower))
    );
  }, [currentMembers, debouncedFilter]);

  if (profileLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left" style={{ maxWidth: '1200px' }}>
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!currentProfile || currentProfile.tipo !== 'LIGA') {
    return null;
  }

  return (
    <>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left" style={{ maxWidth: '1200px' }}>
            <div className="row feed-body">
              <div className="col-xl-12">
                <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                  <div className="card-body p-4">
                    <div className="mb-3">
                      <PageBreadcrumb
                        items={[
                          { label: 'Dashboard', href: '/liga/dashboard' },
                          { label: 'Membros' },
                        ]}
                        onBack={() => navigate('/liga/dashboard')}
                        variant="bootstrap"
                      />
                    </div>
                    <h3 className="fw-700 mb-4">Membros da Liga</h3>

                    <div className="row g-2 mb-3">
                      <div className="col-md-4">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Filtrar por nome..."
                          aria-label="Filtrar membros por nome"
                          value={nameFilter}
                          onChange={(e) => setNameFilter(e.target.value)}
                        />
                      </div>
                    </div>

                    <ul className="nav nav-tabs mb-3" role="tablist">
                      {TABS.map((tab) => (
                        <li key={tab.id} className="nav-item" role="presentation">
                          <button
                            type="button"
                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                            role="tab"
                            aria-selected={activeTab === tab.id}
                          >
                            {tab.label}
                          </button>
                        </li>
                      ))}
                    </ul>

                    <LeagueMembersList
                      members={filteredMembers}
                      loading={loading}
                      error={error}
                      emptyMessage={EMPTY_MESSAGES[activeTab]}
                      alunosIds={alunosIds}
                      selectedIds={selectedIds}
                      savingAlunoId={savingAluno}
                      onToggleSelect={(atletaId) => {
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(atletaId)) next.delete(atletaId);
                          else next.add(atletaId);
                          return next;
                        });
                      }}
                      onToggleAluno={async (atletaId, isAluno) => {
                        if (!ligaId) return;
                        setSavingAluno(atletaId);
                        if (isAluno) {
                          await desmarcarAluno(ligaId, atletaId);
                          setAlunosIds((prev) => { const n = new Set(prev); n.delete(atletaId); return n; });
                        } else {
                          await marcarAluno(ligaId, atletaId);
                          setAlunosIds((prev) => new Set(Array.from(prev).concat(atletaId)));
                        }
                        setSavingAluno(null);
                      }}
                      onMarcarSelecionados={async () => {
                        if (!ligaId || selectedIds.size === 0) return;
                        const ids = Array.from(selectedIds);
                        await marcarAlunosEmMassa(ligaId, ids);
                        setAlunosIds((prev) => new Set(Array.from(prev).concat(ids)));
                        setSelectedIds(new Set());
                      }}
                      onDesmarcarSelecionados={async () => {
                        if (!ligaId || selectedIds.size === 0) return;
                        const ids = Array.from(selectedIds);
                        await desmarcarAlunosEmMassa(ligaId, ids);
                        setAlunosIds((prev) => { const n = new Set(prev); ids.forEach((id) => n.delete(id)); return n; });
                        setSelectedIds(new Set());
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeagueMembersPage;
